<template>
  <productlist
    v-if="!loading && products.length > 0"
    categoryid="extraproducts"
    :mode="mode"
    :count="count"
    :showaddtocart="showaddtocart"
    :showprocessorder="showprocessorder"
    :step="step"
    :postMountedProducts="products"
    :columnFormat="columnformat"
    :showBeforeText="showBeforeText"
    :showAfterText="showAfterText"
    :orderType="orderType"
  />
</template>

<script>
import productlist from "@/components/productlist/ProductList.vue";
import FetchFile from "@/services/fetchFile";
import ExtraSellService from "@/services/extraSell";
import { useCartStore } from "@/store/cart";
import Utils from "@/services/utils";

export default {
  name: "ProductExtraProducts",
  components: {
    productlist,
  },
  data() {
    return {
      products: [],
      loading: false,
      loadGeneration: 0,
    };
  },
  props: {
    mode: {
      type: String,
      default: "products-grid",
    },
    count: {
      type: Number,
      default: 3,
    },
    columnformat: {
      type: String,
      default: "4/8",
    },
    postMountedProducts: {
      type: Array,
      default: () => [],
    },
    showaddtocart: {
      type: Boolean,
      default: true,
    },
    showprocessorder: {
      type: Boolean,
      default: false,
    },
    step: {
      type: Number,
      default: 0,
    },
    showBeforeText: {
      type: Boolean,
      default: false,
    },
    showAfterText: {
      type: Boolean,
      default: false,
    },
    orderType: {
      type: String,
      default: "crosssell",
    },
  },
  computed: {
    cart() {
      return useCartStore().cart;
    },
    lastCartUpdate() {
      return useCartStore().lastUpdate;
    },
    usesExternalProducts() {
      return (
        this.showprocessorder ||
        (Array.isArray(this.postMountedProducts) &&
          this.postMountedProducts.length > 0)
      );
    },
  },
  watch: {
    postMountedProducts: {
      handler(newValue) {
        if (newValue?.length > 0) {
          this.products = newValue;
        }
      },
      immediate: true,
    },
    lastCartUpdate: {
      handler() {
        if (!this.usesExternalProducts) {
          this.loadExtrasellsFromProductDetails();
        }
      },
    },
    products: {
      handler() {
        this.$nextTick(() => {
          setTimeout(() => {
            Utils.handleConditionedContent();
          }, 50);
        });
      },
    },
  },
  mounted() {
    this.setupImageChangeListener();

    if (this.usesExternalProducts) {
      this.products = this.postMountedProducts;
    } else {
      this.loadExtrasellsFromProductDetails();
    }
  },
  beforeUnmount() {
    if (this.imageChangeHandler) {
      document.removeEventListener(
        "change-information-extra-product",
        this.imageChangeHandler,
      );
    }
  },
  methods: {
    setupImageChangeListener() {
      this.imageChangeHandler = (event) => {
        this.products.forEach((product) => {
          if (product.code === event.detail.productCode) {
            switch (event.detail.typeChange) {
              case "images-url":
                product.images.forEach((image) => {
                  image.src = image.src.replace(
                    event.detail.replaceFrom,
                    event.detail.replaceTo,
                  );
                });
                break;
              default:
                break;
            }
          }
        });
      };

      document.addEventListener(
        "change-information-extra-product",
        this.imageChangeHandler,
      );
    },

    fetchProductData(productId) {
      const path = `/data/products/${productId}.json`;

      return new FetchFile().fetch(path, ({ data }) => data).catch(() => null);
    },

    async loadExtrasellsFromProductDetails() {
      if (this.usesExternalProducts) {
        return;
      }

      if (!window.productDetails?.extrasells?.length) {
        this.products = [];
        this.loading = false;
        return;
      }

      const loadGeneration = ++this.loadGeneration;
      this.loading = true;
      this.products = [];

      const extrasells = window.productDetails.extrasells;
      const cartItems = this.cart?.items || [];
      const productDataResults = await Promise.all(
        extrasells.map((extra) => this.fetchProductData(extra.productId)),
      );

      if (loadGeneration !== this.loadGeneration) {
        return;
      }

      let tempExtraProducts = [];
      for (let index = 0; index < extrasells.length; index++) {
        const data = productDataResults[index];
        if (!data) {
          continue;
        }

        tempExtraProducts = ExtraSellService.getExtraProducts(
          data,
          extrasells,
          cartItems,
          tempExtraProducts,
          this.mode,
        );
      }

      this.applyLimitsAndFinalize(tempExtraProducts);
    },

    applyLimitsAndFinalize(products) {
      let finalProducts = [...products];

      if (window.globalConfig?.totalExtraSellInCart > 0) {
        finalProducts = finalProducts.slice(
          0,
          window.globalConfig.totalExtraSellInCart,
        );
      }

      if (this.count > 0) {
        finalProducts = finalProducts.slice(0, this.count);
      }

      this.products = finalProducts;
      this.loading = false;

      setTimeout(() => {
        document.dispatchEvent(new Event("extra-products-loaded"));
        Utils.handleConditionedContent();

        if (finalProducts.length > 0) {
          for (const selector of [
            ".conditional-item-extraproducts",
            ".conditional-item-extra-sell",
          ]) {
            const parentDiv = document.querySelector(selector);
            if (parentDiv) {
              parentDiv.style.display = "block";
            }
          }
        }
      }, 100);
    },
  },
};
</script>
